import React from 'react';
import PropTypes from 'prop-types';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _concat from 'lodash/concat';
import _head from 'lodash/head';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
// HOCs
import { withSize } from 'react-sizeme';
// Material UI
import { makeStyles } from '@material-ui/core/styles';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableSortLabel from '@material-ui/core/TableSortLabel';
// Core components
import Checkbox from 'components/core/Checkbox';
import Tooltip from 'components/core/Tooltip';
// Permissions
import OnlyWith from 'components/permissions/OnlyWith';
// Custom
import { listFeatureColumnShape, sortParamShape } from 'constants/PropTypesShapes';

// Remove 26px (= width of the sort icon with margins) from label
const TableSortLabel = withSize({ refreshRate: 100, refreshMode: 'debounce' })(
  ({
    labelId, active, direction, onClick, size: { width },
  }) => (
    <MuiTableSortLabel
      className="w-100"
      active={active}
      direction={direction}
      onClick={onClick}
    >
      <div style={{ maxWidth: width ? (width - 26) : 'none' }} className="text-truncate">
        <LocalizedMessage id={labelId} />
      </div>
    </MuiTableSortLabel>
  ),
);

TableSortLabel.propTypes = {
  labelId: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onClick: PropTypes.func.isRequired,
  // From `withSize` HOC
  width: PropTypes.number,
};

TableSortLabel.defaultProps = {
  width: null,
};

const useStyles = (stickyHeaderStyle) => makeStyles(() => {
  // Sticky header
  const stickyHeader = { ...(stickyHeaderStyle || {}) };

  return {
    stickyHeader,
  };
});

const TableHead = ({
  columns, sortParam, withSort, nbActions,
  manageSelectAll, selectAllState,
  adjustForCheckbox, adjustForActions, adjustForChoose, adjustForDetails,
  stickyHeaderStyle,
  onSortChange, onSelectAllCheck,
}) => {
  const classes = useStyles(stickyHeaderStyle)();

  const handleSort = (column) => {
    if (column && withSort) {
      const mainSort = _head(sortParam);
      const {
        sortable, field, alias, secondarySorts,
      } = column;
      if (sortable) {
        const dir = mainSort && mainSort.field === field && mainSort.dir === 'asc' ? 'desc' : 'asc';
        onSortChange(_concat([{ field, dir, alias }], secondarySorts || []));
      }
    }
  };

  const mainSort = _head(sortParam);

  return (
    <MuiTableHead>
      <MuiTableRow>
        {adjustForDetails && (
          <MuiTableCell classes={classes} className="table-details" />
        )}
        {(adjustForCheckbox || manageSelectAll) && (
          <MuiTableCell classes={classes} padding="checkbox">
            {manageSelectAll && (
              <Tooltip content={<LocalizedMessage id="tooltip.selectAll" />}>
                <Checkbox
                  checked={!!selectAllState}
                  intermediate={selectAllState === 'intermediate'}
                  onChange={onSelectAllCheck}
                />
              </Tooltip>
            )}
          </MuiTableCell>
        )}
        {_map(_reject(columns, ['hidden', true]), (column, index) => (
          <OnlyWith permissions={column.permissions} key={column.key}>
            <MuiTableCell
              classes={classes}
              className={
                classnames(column.className, {
                  'table-first-details': (index === 0 && adjustForDetails),
                })
              }
              style={{ ...column.style }}
              sortDirection={mainSort && mainSort.field === column.field ? mainSort.dir : false}
            >
              {withSort && column.sortable ? (
                <TableSortLabel
                  labelId={column.labelId}
                  active={mainSort && mainSort.field === column.field}
                  direction={mainSort.field === column.field ? mainSort.dir : 'asc'}
                  onClick={() => handleSort(column)}
                />
              ) : (
                (column.label || column.labelId) && (
                  <div className="text-truncate">
                    {column.label || <LocalizedMessage id={column.labelId} />}
                  </div>
                )
              )}
            </MuiTableCell>
          </OnlyWith>
        ))}
        {adjustForActions && (
          <MuiTableCell classes={classes} className={`table-actions-${nbActions}`} />
        )}
        {adjustForChoose && (
          <MuiTableCell classes={classes} className="table-actions-1" />
        )}
      </MuiTableRow>
    </MuiTableHead>
  );
};

TableHead.propTypes = {
  columns: PropTypes.arrayOf(listFeatureColumnShape).isRequired,
  manageSelectAll: PropTypes.bool,
  selectAllState: PropTypes.oneOf([true, false, 'intermediate']),
  adjustForCheckbox: PropTypes.bool,
  adjustForActions: PropTypes.bool,
  adjustForChoose: PropTypes.bool,
  adjustForDetails: PropTypes.bool,
  nbActions: PropTypes.number,
  withSort: PropTypes.bool,
  sortParam: PropTypes.arrayOf(sortParamShape),
  stickyHeaderStyle: PropTypes.shape({
    top: PropTypes.number.isRequired,
  }),
  onSortChange: PropTypes.func,
  onSelectAllCheck: PropTypes.func,
};

TableHead.defaultProps = {
  manageSelectAll: false,
  selectAllState: false,
  adjustForCheckbox: false,
  adjustForActions: false,
  adjustForChoose: false,
  adjustForDetails: false,
  nbActions: 1,
  withSort: true,
  sortParam: null,
  stickyHeaderStyle: null,
  onSortChange: null,
  onSelectAllCheck: null,
};

export default TableHead;



// WEBPACK FOOTER //
// ./components/core/table/TableHead.js